/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
// Third-party
import { css } from '@emotion/react'
import { Modal, InputNumber, Button, Segmented, message } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
import {
  font,
  weightMedium,
  weightSemiBold,
  paragraphSmall,
} from 'src/components/css'
// Relative
import type {
  BulkConstraintOps,
  BulkConstraintsResult,
  SchedulerConstraint,
} from '../hooks/useSchedulerConstraints'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'
import { extractApiErrorMessage } from '../utils/apiErrorMessage'
import {
  buildAutoBalanceOverrides,
  buildDivisionTargetsBody,
  buildFeasibilityTargetsBody,
  checkFeasibility,
  closestBalancedDivisionTotals,
  computePerTeamCounts,
  constraintsToMatrix,
  divisionGamesSummary,
  divisionTotalFromGamesPerTeam,
  expandSlotsCount,
  feasibleChipDisplay,
  gamesPerTeamFromDivisionTotal,
  mapBackendVerdictToFeasibility,
  mapStructuredFeasibilityError,
  parseDivisionTargetsResponse,
  resolveDivisionTarget,
  type BackendFeasibilityVerdict,
  type FeasibilityResult,
  type MatchupMatrix,
  type SchedulerTargetMode,
} from './matchupMatrixHelpers'

type InputMode = 'per_team' | 'division_total'

// Re-export for callers that import from the modal directly.
export type { MatchupMatrix } from './matchupMatrixHelpers'
export { computeTotalGames } from './matchupMatrixHelpers'

export interface MatchupMatrixModalDivision {
  id: string
  name: string
  teams: { id: string; name: string }[]
}

interface MatchupMatrixModalProps {
  open: boolean
  /** All divisions eligible for matchup config (≥2 teams). The modal owns the in-place division switcher. */
  divisions: MatchupMatrixModalDivision[]
  /** Division to show first when opening. Falls back to the first eligible division if not present. */
  initialDivisionId?: string | null
  /**
   * Uniform default games-per-matchup (from Division.games_per_matchup).
   * Cells matching this count are NOT stored as constraints — only
   * deviating cells become PAIR_MATCHUP_COUNT rows.
   */
  defaultGamesPerMatchup: number
  /**
   * All pair_matchup_count constraints in this flight (across every
   * division, including globals with division_uids=null). The modal
   * filters them to the currently-selected division by matching team_uids
   * to the division's teams.
   */
  existingConstraints: SchedulerConstraint[]
  /**
   * Active time-slot templates for the current flight. Used by the
   * feasibility chip to estimate concrete-slot capacity given the flight's
   * date range. When omitted (or empty), feasibility chips fall back to
   * neutral / informational only.
   */
  timeSlots?: TimeSlot[]
  flightStartDate?: string | null
  flightEndDate?: string | null
  /**
   * Flight UID — required for the backend feasibility-check call. When
   * omitted, the modal falls back to its client-side estimate only.
   */
  flightId?: string | null
  onCancel: () => void
  /**
   * Called after all add/update/delete API operations resolve. May be async
   * (e.g. the parent refetches constraints + division targets, then closes) —
   * handleSave awaits it before clearing `saving` so Save can't re-fire into a
   * duplicate POST while the refetch is still in flight.
   */
  onSaved: () => void | Promise<void>
  /**
   * Bulk add/update/delete in a single atomic call. Replaces the per-
   * row fan-out — partial failure rolls back so the matrix never ends
   * up half-saved.
   */
  bulkSaveConstraints: (
    ops: BulkConstraintOps
  ) => Promise<BulkConstraintsResult>
}

const MIN_GAMES = 1
// No upper cap — leagues can legitimately want very high counts
// (e.g. 50 games per pair for high-frequency tournaments). Keep the
// InputNumber components un-`max`-ed so admins aren't artificially
// blocked.

// Modal width scales with division count, not team count — the new
// layout shows one card per division, no matrix. Single-division leagues
// get a compact ~480px modal; multi-division leagues widen so cards
// stay side-by-side without wrapping. Capped at MODAL_MAX_WIDTH.
const MODAL_MIN_WIDTH = 480
const MODAL_MAX_WIDTH = 960
const MODAL_PER_CARD_WIDTH = 280
const MODAL_HORIZONTAL_PADDING = 80

const computeModalWidth = (divisionCount: number): number => {
  if (divisionCount <= 1) {
    return MODAL_MIN_WIDTH
  }
  // Aim to fit up to 3 cards on one row before wrapping.
  const cols = Math.min(3, divisionCount)
  const desired = cols * MODAL_PER_CARD_WIDTH + MODAL_HORIZONTAL_PADDING
  return Math.min(MODAL_MAX_WIDTH, Math.max(MODAL_MIN_WIDTH, desired))
}

/**
 * Infer the modal's "default games per matchup" from an existing matrix
 * by taking the mode (most-common value). Falls back to `fallback` when
 * the matrix is empty.
 *
 * Why: the wizard hardcodes `defaultGamesPerMatchup = 1` as a prop, but
 * the actual saved state is encoded in pair_matchup_count rows. When a
 * flight was previously saved with default=5, every cell in the matrix
 * holds 5 — so 5 IS the user's effective default. Reading that mode back
 * keeps the input in sync with what the user last persisted.
 */
const inferDefaultFromMatrix = (
  matrix: MatchupMatrix,
  fallback: number
): number => {
  const values = Object.values(matrix)
  if (values.length === 0) {
    return fallback
  }
  const freq: Record<number, number> = {}
  for (const v of values) {
    freq[v] = (freq[v] ?? 0) + 1
  }
  let bestVal = values[0]
  let bestCount = 0
  for (const [v, c] of Object.entries(freq)) {
    if (c > bestCount) {
      bestCount = c
      bestVal = Number(v)
    }
  }
  return bestVal
}

const modalStyles = css`
  .ant-modal-content {
    background-color: ${colors.SECONDARY_300};
    border-radius: 8px;
  }
  .ant-modal-header {
    background-color: ${colors.SECONDARY_300};
    border-bottom: 1px solid ${colors.SECONDARY_200};
  }
  .ant-modal-title {
    color: ${colors.WHITE};
  }
  .ant-modal-close-x {
    color: ${colors.WHITE};
  }
  .ant-modal-footer {
    background-color: ${colors.SECONDARY_300};
    border-top: 1px solid ${colors.SECONDARY_200};
  }
  .ant-input-number {
    background-color: ${colors.SECONDARY_200} !important;
    border-color: ${colors.SECONDARY_100} !important;
    border-radius: 6px !important;
    box-shadow: none !important;
  }
  .ant-input-number-input {
    color: ${colors.WHITE} !important;
    text-align: center;
  }
  .ant-input-number:hover,
  .ant-input-number-focused {
    border-color: ${colors.PRIMARY} !important;
  }
`

const titleStyle = css`
  ${font.title}
  ${weightSemiBold}
  font-size: 18px;
  color: ${colors.WHITE};
  margin: 0;
`

const defaultGamesInputStyle = css`
  width: 120px;
`

// Used inside ant `message.*` toasts to bypass the global
// `text-transform: capitalize` rule applied to .ant-message-custom-content
// in index.scss. Toast copy is full sentences, not capitalised words.
const toastContentStyle = css`
  text-transform: none;
`

// ---------------------------------------------------------------------------
// Section + division-card + chip styles.
// ---------------------------------------------------------------------------

const derivedMathStyle = css`
  ${font.body}
  ${paragraphSmall}
  color: ${colors.NEUTRAL_70};
  margin: 0;
  line-height: 1.4;
`

const divisionCardGridStyle = css`
  display: grid;
  /* auto-fit (vs. auto-fill) collapses empty tracks so a single card
     expands to fill the row instead of leaving dead space on the right. */
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  margin-top: 8px;
`

const divisionCardStyle = css`
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
  padding: 14px 16px;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const divisionCardLabelStyle = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 11px;
  color: ${colors.WHITE};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 2px;
`

const divisionCardInputRowStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
`

const sectionLabelStyle = css`
  ${font.body}
  ${weightMedium}
  font-size: 13px;
  color: ${colors.WHITE};
  letter-spacing: 0.2px;
`

// Feasibility chips — three states, color-coded.
const feasibilityChipBase = css`
  ${font.body}
  ${paragraphSmall}
  margin: 0;
  padding: 10px 12px;
  border-radius: 6px;
  line-height: 1.45;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const feasibilityChipFeasibleStyle = css`
  ${feasibilityChipBase};
  background: rgba(16, 185, 129, 0.08);
  border-left: 3px solid ${colors.SUCCESS_200};
  color: ${colors.SUCCESS_200};
`

const feasibilityChipWarnStyle = css`
  ${feasibilityChipBase};
  background: rgba(234, 179, 8, 0.08);
  border-left: 3px solid ${colors.WARNING_200};
  color: ${colors.WARNING_200};
`

const feasibilityChipErrorStyle = css`
  ${feasibilityChipBase};
  background: rgba(239, 68, 68, 0.08);
  border-left: 3px solid ${colors.ERROR_200};
  color: ${colors.ERROR_200};
`

const feasibilityChipActionsStyle = css`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

// Segmented control styling — Antd v5's default palette doesn't fit the
// modal's dark surface. Override the track + thumb + label colors so the
// toggle reads cleanly against `colors.SECONDARY_300`.
const segmentedToggleStyle = css`
  &.ant-segmented {
    background-color: ${colors.SECONDARY_200};
    padding: 2px;
    border-radius: 6px;
  }
  .ant-segmented-item {
    color: ${colors.NEUTRAL_30};
    transition: color 0.15s;
  }
  .ant-segmented-item:hover:not(.ant-segmented-item-disabled) {
    color: ${colors.WHITE};
  }
  .ant-segmented-item-selected {
    background-color: ${colors.PRIMARY} !important;
    color: ${colors.SECONDARY_300} !important;
    font-weight: 600;
  }
  .ant-segmented-item-label {
    line-height: 24px;
  }
`

const footerKpiLineStyle = css`
  ${font.body}
  ${paragraphSmall}
  color: ${colors.NEUTRAL_70};
  margin: 0;
  flex: 1;
  min-width: 0;
`

const footerKpiWarnStyle = css`
  ${footerKpiLineStyle};
  color: ${colors.WARNING_200};
`

/**
 * Modal for configuring per-division matchup counts.
 *
 * Each cell that differs from the uniform `games_per_matchup` default
 * becomes a PAIR_MATCHUP_COUNT constraint row (added/updated/deleted via
 * the existing constraints API). Cells matching the default are NOT
 * stored — they fall back to the uniform default at solve time.
 *
 * Division selection is owned by this modal: the parent passes the full
 * list of eligible divisions and the modal renders a dropdown so the
 * admin can switch divisions in-place without closing.
 *
 * Each cell can additionally be marked "Apply to all divisions" — a
 * global override stored as a constraint with division_uids=null.
 *
 * On open: paint existing PAIR_MATCHUP_COUNT constraints into the grid
 *   (filtered to those belonging to the current division's teams).
 * On save: diff against existing and call addConstraint / updateConstraint
 *   / deleteConstraint as needed, then onSaved().
 */
const MatchupMatrixModal: React.FC<MatchupMatrixModalProps> = ({
  open,
  divisions,
  initialDivisionId: _initialDivisionId,
  defaultGamesPerMatchup,
  existingConstraints,
  timeSlots = [],
  flightStartDate = null,
  flightEndDate = null,
  flightId: _flightId = null,
  onCancel,
  onSaved,
  // bulkSaveConstraints is still accepted (parent passes it) but no longer
  // used: saves now go through the per-division target endpoint, not
  // pair_matchup_count bulk writes.
}) => {
  'use memo'

  // ---- Division selection (in-modal) ----
  const eligibleDivisions = useMemo(
    () => divisions.filter((d) => d.teams.length >= 2),
    [divisions]
  )

  // Per-division edit state. Each entry holds the in-progress matrix +
  // gamesPerMatchup for that division. The matrix is populated silently by
  // auto-balance writes; the user never edits it directly in v3.
  //
  // Why a map: admins configure several divisions in one modal session —
  // set Division1=18, set Division2=14, then Save once. The map keeps each
  // division's edits in memory until save.
  type DivisionEdits = {
    matrix: MatchupMatrix
    gamesPerMatchup: number
  }
  const [editsByDivision, setEditsByDivision] = useState<
    Record<string, DivisionEdits>
  >({})
  const [saving, setSaving] = useState(false)
  // Footer "Apply same to all divisions" input — ephemeral. Holds the
  // pending value while the user is typing; cleared after fan-out.
  const [applyAllInput, setApplyAllInput] = useState<number | null>(null)

  // Per-division input mode — `per_team` (default, canonical) or
  // `division_total`. UI-only state; the modal always resolves to a
  // per-team value before mutating editsByDivision, so the backend
  // contract is unchanged.
  const [inputModeByDivision, setInputModeByDivision] = useState<
    Record<string, InputMode>
  >({})

  // Per-division in-flight invalid division-total draft. Set when the
  // user types a total that doesn't divide evenly into team count;
  // cleared when they enter a valid total or click an "Apply X"
  // suggestion. Never written to editsByDivision — the canonical
  // proposal stays at the previous valid value.
  const [invalidTotalDraft, setInvalidTotalDraft] = useState<
    Record<string, number>
  >({})

  // Per-division explicit per-team target the admin typed (or that was seeded
  // from a saved games_per_team / division_total target). This is the SOURCE OF
  // TRUTH for the displayed value, the feasibility chip, the footer totals, and
  // the saved target_number — NOT the legacy `matrix`/`gamesPerMatchup`, which
  // can only represent a FULL round-robin or above (`gamesPerMatchup >= 1`). A
  // partial round-robin (games_per_team < opponents, e.g. 3 with 8 teams) is
  // valid — the backend resolves balanced per-pair counts from the target — but
  // the matrix would clamp it UP to a full round-robin (7), so we never derive
  // the per-team value from the matrix once an explicit target exists.
  const [perTeamTargetByDivision, setPerTeamTargetByDivision] = useState<
    Record<string, number>
  >({})

  const getInputMode = useCallback(
    (divId: string): InputMode => inputModeByDivision[divId] ?? 'per_team',
    [inputModeByDivision]
  )

  // Helper: filter the parent's full constraints to the pair_matchup_count
  // rows whose two team_uids both belong to one specific division. Used
  // both for initial-entry materialization and the per-division save diff.
  const filterConstraintsForDivision = useCallback(
    (divisionId: string): SchedulerConstraint[] => {
      const div = eligibleDivisions.find((d) => d.id === divisionId)
      if (!div) {
        return []
      }
      const teamIds = new Set(div.teams.map((t) => t.id))
      return existingConstraints.filter((c) => {
        if (c.type.code !== 'pair_matchup_count') {
          return false
        }
        const teamUids = c.team_uids ?? []
        if (teamUids.length !== 2) {
          return false
        }
        return teamIds.has(teamUids[0]) && teamIds.has(teamUids[1])
      })
    },
    [existingConstraints, eligibleDivisions]
  )

  // The "initial" entry for a division — what it'd look like with no
  // user edits. Used as the fallback when editsByDivision[id] is missing.
  //
  // We infer `gamesPerMatchup` from the matrix's mode (most-common value)
  // rather than blindly using the prop default. The prop default is a
  // hardcoded `1` from the wizard; the actual saved state is encoded in
  // pair_matchup_count rows. Without this inference, opening a flight
  // that was saved with default=5 would show the input as 1 even though
  // every cell in the matrix correctly displays 5 — confusing.
  const buildInitialEntry = useCallback(
    (divisionId: string): DivisionEdits => {
      const cs = filterConstraintsForDivision(divisionId)
      const initialMatrix = constraintsToMatrix(cs)
      const inferredDefault = inferDefaultFromMatrix(
        initialMatrix,
        defaultGamesPerMatchup
      )
      return {
        matrix: initialMatrix,
        gamesPerMatchup: inferredDefault,
      }
    },
    [filterConstraintsForDivision, defaultGamesPerMatchup]
  )

  // Mutate one division's entry. First-touch seeds from existing constraints.
  const mutateForDivision = useCallback(
    (divId: string, updater: (prev: DivisionEdits) => DivisionEdits) => {
      setEditsByDivision((prev) => {
        const baseline = prev[divId] ?? buildInitialEntry(divId)
        return { ...prev, [divId]: updater(baseline) }
      })
    },
    [buildInitialEntry]
  )

  // Read one division's entry — explicit map value or freshly-built initial.
  // Memoized callback so child renders don't churn.
  const getEntry = useCallback(
    (divId: string): DivisionEdits => {
      return editsByDivision[divId] ?? buildInitialEntry(divId)
    },
    [editsByDivision, buildInitialEntry]
  )

  // Wipe the editing buffer when the modal opens — fresh session each
  // time. (destroyOnClose handles unmount; belt-and-suspenders.)
  // Input mode + invalid-total draft are also UI-only state; same reset.
  useEffect(() => {
    if (open) {
      setEditsByDivision({})
      setApplyAllInput(null)
      setInputModeByDivision({})
      setInvalidTotalDraft({})
      setPerTeamTargetByDivision({})
    }
  }, [open])

  // ---- Per-division "Games per team" handlers ----
  //
  // The user types a per-team target. We back-solve to per-pair count via
  // floor-division, then attempt auto-balance silently:
  //   - If target divides cleanly → uniform matrix (no per-pair overrides).
  //   - If target needs balancing & `buildAutoBalanceOverrides` says yes →
  //     write the boosted per-pair overrides into entry.matrix.
  //   - If parity gate fails → leave the matrix untouched; the feasibility
  //     chip surfaces the closest clean values as one-click apply buttons.

  const applyDivisionTarget = useCallback(
    (divId: string, gamesPerTeam: number) => {
      const div = eligibleDivisions.find((d) => d.id === divId)
      if (!div || div.teams.length < 2) {
        return
      }
      // Any successful per-team mutation supersedes a pending invalid
      // total draft. Clearing here covers every caller — typed
      // per-team input, the "Apply X" suggestion buttons in both the
      // feasibility chip and the InvalidTotalChip, and the
      // "Apply to all" fan-out.
      setInvalidTotalDraft((prev) => {
        if (!(divId in prev)) {
          return prev
        }
        const next = { ...prev }
        delete next[divId]
        return next
      })
      const teamUids = div.teams.map((t) => t.id)
      const opponents = div.teams.length - 1
      const target = Math.max(0, gamesPerTeam)

      // Record the explicit per-team target as the source of truth. Everything
      // user-facing (input value, chip, footer totals, saved target_number)
      // reads this — never the matrix, which can't represent a partial
      // round-robin (target < opponents) and would clamp it up to a full one.
      setPerTeamTargetByDivision((prev) => ({ ...prev, [divId]: target }))

      // The matrix mutations below still run so divisions that DID arrive with
      // explicit pair_matchup_count rows keep a coherent legacy entry; they no
      // longer drive the displayed/saved per-team value.

      // Clean integer division → uniform per-pair count, matrix empty.
      if (opponents > 0 && target % opponents === 0) {
        const perPair = Math.max(MIN_GAMES, target / opponents)
        mutateForDivision(divId, (entry) => ({
          ...entry,
          matrix: {},
          gamesPerMatchup: perPair,
        }))
        return
      }

      // Try auto-balance (boost some pairs to hit the target).
      const proposal = buildAutoBalanceOverrides(teamUids, target)
      if (proposal && proposal.achievable) {
        const nextMatrix: MatchupMatrix = {}
        for (const key of proposal.boostedPairKeys) {
          nextMatrix[key] = proposal.baseGamesPerMatchup + 1
        }
        mutateForDivision(divId, (entry) => ({
          ...entry,
          matrix: nextMatrix,
          gamesPerMatchup: Math.max(MIN_GAMES, proposal.baseGamesPerMatchup),
        }))
        return
      }

      // Parity gate fails — record the per-pair floor, no boosts. The
      // chip will tell the user the closest achievable targets.
      const baseFloor =
        opponents > 0
          ? Math.max(MIN_GAMES, Math.floor(target / opponents))
          : MIN_GAMES
      mutateForDivision(divId, (entry) => ({
        ...entry,
        matrix: {},
        gamesPerMatchup: baseFloor,
      }))
    },
    [eligibleDivisions, mutateForDivision]
  )

  const handleDivisionPerTeamChange = useCallback(
    (divId: string, value: number | null) => {
      if (value == null) {
        return
      }
      // applyDivisionTarget clears any pending invalid-total draft for
      // this division, so we don't need to do it here.
      applyDivisionTarget(divId, value)
    },
    [applyDivisionTarget]
  )

  // Division-total mode: convert the user's typed total to a per-team
  // value, then route through applyDivisionTarget (the single mutation
  // surface). When the total can't split evenly across teams, store the
  // typed value in `invalidTotalDraft` so the input still reflects it
  // and the chip can surface suggestions — but do NOT mutate the
  // canonical proposal.
  const handleDivisionTotalChange = useCallback(
    (divId: string, totalGames: number | null) => {
      if (totalGames == null) {
        return
      }
      const div = eligibleDivisions.find((d) => d.id === divId)
      if (!div) {
        return
      }
      const perTeam = gamesPerTeamFromDivisionTotal(
        div.teams.length,
        totalGames
      )
      if (perTeam == null) {
        setInvalidTotalDraft((prev) => ({ ...prev, [divId]: totalGames }))
        return
      }
      // Valid total — applyDivisionTarget clears the pending draft.
      applyDivisionTarget(divId, perTeam)
    },
    [eligibleDivisions, applyDivisionTarget]
  )

  const handleInputModeChange = useCallback(
    (divId: string, mode: InputMode) => {
      setInputModeByDivision((prev) => ({ ...prev, [divId]: mode }))
      // Switching modes resets any pending invalid draft so the new
      // mode starts clean.
      setInvalidTotalDraft((prev) => {
        if (!(divId in prev)) {
          return prev
        }
        const next = { ...prev }
        delete next[divId]
        return next
      })
    },
    []
  )

  const handleApplyAllToDivisions = useCallback(
    (value: number | null) => {
      if (value == null) {
        return
      }
      for (const d of eligibleDivisions) {
        applyDivisionTarget(d.id, value)
      }
      setApplyAllInput(null)
    },
    [eligibleDivisions, applyDivisionTarget]
  )

  // ---- Load saved per-division targets when the modal opens ----
  //
  // The canonical saved state is now a SchedulerDivisionTarget row per
  // division (not pair_matchup_count rows). Seed each division's input mode +
  // value from its saved target so reopening reflects what was saved:
  //   - games_per_team  → per_team mode, seed the per-team value
  //   - division_total  → division_total mode, seed the typed total
  //   - fill_feasible_slots / no row → leave the default (untouched → fills)
  // Runs after the open-reset effect (later effect = later run), so it seeds
  // onto a clean slate. Failures fall back silently to defaults.
  useEffect(() => {
    if (!open || !_flightId) {
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await req(
          `/scheduler/flights/${_flightId}/division-targets`
        )
        if (cancelled) {
          return
        }
        // Unwrap the `{ data: [...] }` envelope (req returns the response whose
        // `.data` is the body) into validated rows.
        const rows = parseDivisionTargetsResponse(data)
        const modes: Record<string, InputMode> = {}
        for (const row of rows) {
          const { division_uid: divId, target_mode: mode } = row
          const num = row.target_number ?? null
          if (mode === 'games_per_team' && num && num >= 1) {
            modes[divId] = 'per_team'
            applyDivisionTarget(divId, num)
          } else if (mode === 'division_total' && num && num >= 1) {
            modes[divId] = 'division_total'
            handleDivisionTotalChange(divId, num)
          }
          // fill_feasible_slots → leave default (no seed).
        }
        if (!cancelled && Object.keys(modes).length > 0) {
          setInputModeByDivision((prev) => ({ ...prev, ...modes }))
        }
      } catch {
        // Ignore — modal shows defaults when targets can't be loaded.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, _flightId, applyDivisionTarget, handleDivisionTotalChange])

  // ---- Derived per-division summaries ----

  // For each division, compute the effective "games per team" from the
  // current matrix + base. Same value used to display in the input and to
  // feed the feasibility check.
  const divisionEntries = useMemo(
    () =>
      eligibleDivisions.map((d) => {
        const entry = getEntry(d.id)
        const teamCount = d.teams.length
        // An explicit per-team target is authoritative — never clamp it through
        // the matrix (which can't represent a partial round-robin). See
        // divisionGamesSummary (SN-7897).
        const { perTeam, divGames } = divisionGamesSummary(
          d.teams,
          perTeamTargetByDivision[d.id] ?? null,
          entry.matrix,
          entry.gamesPerMatchup
        )
        return { div: d, entry, perTeam, divGames, teamCount }
      }),
    [eligibleDivisions, getEntry, perTeamTargetByDivision]
  )

  // Whether the admin has set an EXPLICIT target for this division in this
  // session — either a direct edit, or a saved target seeded on open (the load
  // effect seeds games_per_team via applyDivisionTarget → editsByDivision, and
  // division_total via handleDivisionTotalChange → editsByDivision/invalidTotalDraft).
  // A division that is neither edited nor seeded is "untouched": its canonical
  // target is fill_feasible_slots, matching what generation stamps. Critically
  // it is NOT games_per_team derived from the games_per_matchup=1 baseline —
  // that derived (n-1) value is display math, never the target.
  const divisionHasExplicitTarget = useCallback(
    (divId: string): boolean =>
      divId in editsByDivision || divId in invalidTotalDraft,
    [editsByDivision, invalidTotalDraft]
  )

  // Resolve a division's target. Untouched → fill_feasible_slots (preview ==
  // generation). Explicit `per_team` → games_per_team; explicit `division_total`
  // → division_total (the typed total, uneven allowed — invalidTotalDraft holds
  // the uneven value; an even total is derived from the per-team value).
  const resolveTargetForDivision = useCallback(
    (
      divId: string
    ): { target_mode: SchedulerTargetMode; target_number: number | null } => {
      if (!divisionHasExplicitTarget(divId)) {
        return { target_mode: 'fill_feasible_slots', target_number: null }
      }
      const de = divisionEntries.find((e) => e.div.id === divId)
      const perTeam = de?.perTeam ?? 0
      const teamCount = de?.teamCount ?? 0
      const mode = getInputMode(divId)
      let divisionTotal: number | null = null
      if (mode === 'division_total') {
        divisionTotal =
          invalidTotalDraft[divId] != null
            ? invalidTotalDraft[divId]
            : divisionTotalFromGamesPerTeam(teamCount, perTeam)
      }
      return resolveDivisionTarget({
        inputMode: mode,
        perTeam,
        divisionTotal,
      })
    },
    [
      divisionHasExplicitTarget,
      divisionEntries,
      getInputMode,
      invalidTotalDraft,
    ]
  )

  // ---- Slot capacity + feasibility chips ----

  const totalAvailableSlots = useMemo(() => {
    if (!flightStartDate || !flightEndDate) {
      return 0
    }
    return expandSlotsCount(timeSlots, flightStartDate, flightEndDate)
  }, [timeSlots, flightStartDate, flightEndDate])

  // Client-side feasibility estimate — runs synchronously on every state
  // change. Covers the "no flight" / "before backend response lands" /
  // "backend network failure" cases. Conservative (raw slot count, no
  // facility/per-day refinement) — backend has the final say.
  const clientFeasibilityByDivision = useMemo(() => {
    if (totalAvailableSlots === 0) {
      return new Map<string, FeasibilityResult>()
    }
    const map = new Map<string, FeasibilityResult>()
    let allocatedSoFar = 0
    for (const { div, perTeam, divGames, teamCount } of divisionEntries) {
      const result = checkFeasibility({
        divisionTeamCount: teamCount,
        gamesPerTeam: perTeam,
        totalAvailableSlots,
        alreadyAllocatedSlots: allocatedSoFar,
        teamUids: div.teams.map((t) => t.id),
      })
      map.set(div.id, result)
      allocatedSoFar += divGames
    }
    return map
  }, [divisionEntries, totalAvailableSlots])

  // Backend feasibility — debounced 300ms after the last edit settles.
  // Authoritative when available; falls back silently to the client-side
  // estimate on network failure or when flightId is unknown.
  const [backendFeasibilityByDivision, setBackendFeasibilityByDivision] =
    useState<Map<string, FeasibilityResult>>(new Map())
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!_flightId || !open) {
      return
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    // Snapshot the per-division target — the modal sends the *exact* unsaved
    // target so the backend can verdict it (target contract; the backend
    // derives balanced per-pair counts) without persisting anything.
    const divisionsBody = buildFeasibilityTargetsBody(
      eligibleDivisions.map((d) => ({
        divisionId: d.id,
        target: resolveTargetForDivision(d.id),
      }))
    )
    // Per-division team UID lookup so balance_impossible can compute
    // closest-below / closest-above for the chip's suggestion buttons
    // without re-running the modal's auto-balance side effects.
    const teamUidsByDivision = new Map(
      eligibleDivisions.map((d) => [d.id, d.teams.map((t) => t.id)])
    )
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await req(
          `/scheduler/flights/${_flightId}/feasibility-check`,
          {
            method: 'POST',
            body: JSON.stringify({ divisions: divisionsBody }),
          }
        )
        const next = new Map<string, FeasibilityResult>()
        const list: BackendFeasibilityVerdict[] = data?.divisions ?? []
        // D1/D4: the scheduler stamps `feasibility_degraded` when its
        // partition refinement failed and the numbers are optimistic ceiling
        // estimates — feasible chips render amber (not confident green) then.
        const degraded = Boolean(data?.feasibility_degraded)
        for (const verdict of list) {
          const teamUids = teamUidsByDivision.get(verdict.division_uid) ?? []
          next.set(
            verdict.division_uid,
            mapBackendVerdictToFeasibility(verdict, teamUids, { degraded })
          )
        }
        setBackendFeasibilityByDivision(next)
      } catch (err) {
        // A STRUCTURED scheduler rejection (api2 forwards python's 422
        // detail verbatim since D5; req() surfaces 4xx bodies top-level on
        // the error) must render RED backend verdicts for the named
        // divisions — pre-fix any error silently fell back to the
        // OPTIMISTIC local estimate. Only an unstructured failure (network,
        // unknown shape) keeps the labeled local estimate, the documented
        // offline fallback.
        setBackendFeasibilityByDivision(
          mapStructuredFeasibilityError(err) ?? new Map()
        )
      }
    }, 300)
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [_flightId, open, eligibleDivisions, resolveTargetForDivision])

  // Backend takes precedence; fall back to the client estimate per-div.
  const feasibilityByDivision = useMemo(() => {
    if (backendFeasibilityByDivision.size === 0) {
      return clientFeasibilityByDivision
    }
    const merged = new Map<string, FeasibilityResult>(
      clientFeasibilityByDivision
    )
    for (const [k, v] of backendFeasibilityByDivision) {
      merged.set(k, v)
    }
    return merged
  }, [clientFeasibilityByDivision, backendFeasibilityByDivision])

  const handleSave = useCallback(async () => {
    if (!_flightId) {
      message.error(
        <span css={toastContentStyle}>
          Can&apos;t save games per team without a flight context.
        </span>
      )
      return
    }
    setSaving(true)
    try {
      // The canonical source of truth is now the per-division TARGET, not
      // auto-balanced pair_matchup_count rows. We POST a target row per
      // division the admin touched (per-team edits land in editsByDivision;
      // uneven division-totals land in invalidTotalDraft). The backend
      // (scheduler-python) derives balanced per-pair counts from the target,
      // and the endpoint cleans up this division's legacy pair rows so they
      // can't contaminate the target as hidden overrides.
      const touchedIds = new Set<string>([
        ...Object.keys(editsByDivision),
        ...Object.keys(invalidTotalDraft),
      ])
      const targetEntries = [...touchedIds]
        .filter((id) => eligibleDivisions.some((d) => d.id === id))
        .map((divisionId) => ({
          divisionId,
          target: resolveTargetForDivision(divisionId),
        }))

      if (targetEntries.length === 0) {
        message.info(
          <span css={toastContentStyle}>
            Nothing to save — set a games-per-team or division-total target.
          </span>
        )
        await onSaved()
        return
      }

      await req(`/scheduler/flights/${_flightId}/division-targets`, {
        method: 'POST',
        body: JSON.stringify({
          targets: buildDivisionTargetsBody(targetEntries),
          cleanup_legacy_pairs: true,
        }),
      })

      const acrossCopy =
        targetEntries.length > 1
          ? ` across ${targetEntries.length} divisions`
          : ''
      message.success(
        <span css={toastContentStyle}>
          {`Saved games per team${acrossCopy}.`}
        </span>
      )
      await onSaved()
    } catch (err) {
      const detail = extractApiErrorMessage(err)
      message.error(
        <span css={toastContentStyle}>
          {`Failed to save games per team: ${detail}`}
        </span>
      )
    } finally {
      setSaving(false)
    }
  }, [
    _flightId,
    editsByDivision,
    invalidTotalDraft,
    eligibleDivisions,
    resolveTargetForDivision,
    onSaved,
  ])

  // Footer KPI summary (across all divisions). `divGames` already prefers the
  // explicit target (partial-RR aware) over the full-RR matrix.
  const totalLeagueGames = useMemo(() => {
    let total = 0
    for (const { divGames } of divisionEntries) {
      total += divGames
    }
    return total
  }, [divisionEntries])

  const leaguePerTeamSummary = useMemo(() => {
    const counts: number[] = []
    for (const { div, entry, perTeam, teamCount } of divisionEntries) {
      if (perTeamTargetByDivision[div.id] != null) {
        // Explicit target → balanced: every team plays `perTeam`.
        for (let i = 0; i < teamCount; i += 1) {
          counts.push(perTeam)
        }
        continue
      }
      const divCounts = computePerTeamCounts(
        div.teams,
        entry.matrix,
        entry.gamesPerMatchup
      )
      for (const v of Object.values(divCounts)) {
        counts.push(v)
      }
    }
    if (counts.length === 0) {
      return { mode: 0, varies: false }
    }
    const freq: Record<number, number> = {}
    for (const v of counts) {
      freq[v] = (freq[v] ?? 0) + 1
    }
    let best = counts[0]
    let bestCount = 0
    for (const [v, c] of Object.entries(freq)) {
      if (c > bestCount) {
        bestCount = c
        best = Number(v)
      }
    }
    const varies = counts.some((c) => c !== best)
    return { mode: best, varies }
  }, [divisionEntries, perTeamTargetByDivision])

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={<h3 css={titleStyle}>Games per Team</h3>}
      width={computeModalWidth(eligibleDivisions.length)}
      footer={
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            padding: 4px 0;
          `}
        >
          <p
            css={
              leaguePerTeamSummary.varies
                ? footerKpiWarnStyle
                : footerKpiLineStyle
            }
          >
            {eligibleDivisions.length} division
            {eligibleDivisions.length === 1 ? '' : 's'} · {totalLeagueGames}{' '}
            league game
            {totalLeagueGames === 1 ? '' : 's'} ·{' '}
            {leaguePerTeamSummary.varies
              ? `${leaguePerTeamSummary.mode} per team (varies)`
              : `${leaguePerTeamSummary.mode} per team`}
          </p>
          <div
            css={css`
              display: flex;
              gap: 10px;
            `}
          >
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type='primary'
              onClick={handleSave}
              loading={saving}
              disabled={saving}
            >
              Save
            </Button>
          </div>
        </div>
      }
      destroyOnClose
      css={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 18px;
        `}
      >
        <p
          css={css`
            ${font.body}
            ${weightSemiBold}
            font-size: 14px;
            color: ${colors.WHITE};
            margin: 0;
          `}
        >
          Set the target for each division
        </p>

        {/* "Apply same to all divisions" — surfaces as the primary
            shortcut for the common "everyone plays N games" case.
            Renders only in multi-division leagues; single-division
            leagues fall straight through to the per-division card. */}
        {eligibleDivisions.length > 1 && (
          <div
            css={css`
              background: ${colors.SECONDARY_300};
              border-radius: 8px;
              padding: 14px 16px;
              display: flex;
              align-items: center;
              gap: 12px;
              flex-wrap: wrap;
            `}
          >
            <span css={sectionLabelStyle}>
              Apply same value to all divisions:
            </span>
            <span
              css={css`
                ${derivedMathStyle};
                margin: 0;
              `}
            >
              Games per team
            </span>
            <InputNumber
              min={0}
              value={applyAllInput ?? undefined}
              placeholder='—'
              onChange={(v) => setApplyAllInput(v ?? null)}
              onPressEnter={() => handleApplyAllToDivisions(applyAllInput)}
              disabled={saving}
              css={defaultGamesInputStyle}
            />
            <Button
              size='small'
              type='primary'
              disabled={saving || applyAllInput == null}
              onClick={() => handleApplyAllToDivisions(applyAllInput)}
            >
              Apply
            </Button>
          </div>
        )}

        {/* Per-division detail — only shown after the "apply to all"
            shortcut, since the common case is uniform-across-divisions. */}
        {eligibleDivisions.length > 1 && (
          <p
            css={css`
              ${derivedMathStyle};
              margin: 0;
            `}
          >
            Or set each division individually:
          </p>
        )}
        <div css={divisionCardGridStyle}>
          {divisionEntries.map(({ div, perTeam, teamCount }) => {
            const feasibility = feasibilityByDivision.get(div.id)
            const inputMode = getInputMode(div.id)
            const invalidDraft = invalidTotalDraft[div.id]
            return (
              <DivisionTargetCard
                key={div.id}
                divName={div.name}
                teamCount={teamCount}
                perTeam={perTeam}
                isUntouched={!divisionHasExplicitTarget(div.id)}
                feasibility={feasibility}
                hasSlotData={totalAvailableSlots > 0}
                disabled={saving}
                inputMode={inputMode}
                invalidTotalDraft={invalidDraft}
                onInputModeChange={(mode) =>
                  handleInputModeChange(div.id, mode)
                }
                onPerTeamChange={(value) =>
                  handleDivisionPerTeamChange(div.id, value)
                }
                onTotalChange={(value) =>
                  handleDivisionTotalChange(div.id, value)
                }
                onApplySuggestion={(value) =>
                  applyDivisionTarget(div.id, value)
                }
              />
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// DivisionTargetCard — one card per division. Owns its own input UX +
// feasibility chip rendering. Stateless; parent passes the current target +
// feasibility result.
// ---------------------------------------------------------------------------

interface DivisionTargetCardProps {
  divName: string
  teamCount: number
  perTeam: number
  /**
   * No saved target and no edit this session → the division fills its feasible
   * slots by default. The inputs render blank (the `perTeam`/total math is only
   * a baseline, NOT the target) until the admin enters a value.
   */
  isUntouched: boolean
  feasibility: FeasibilityResult | undefined
  hasSlotData: boolean
  disabled: boolean
  inputMode: InputMode
  /** Set when the user typed an invalid division total; undef otherwise. */
  invalidTotalDraft: number | undefined
  onInputModeChange: (mode: InputMode) => void
  onPerTeamChange: (value: number | null) => void
  onTotalChange: (value: number | null) => void
  /** Per-team value to apply when the chip suggestion is clicked. */
  onApplySuggestion: (value: number) => void
}

const DivisionTargetCard: React.FC<DivisionTargetCardProps> = ({
  divName,
  teamCount,
  perTeam,
  isUntouched,
  feasibility,
  hasSlotData,
  disabled,
  inputMode,
  invalidTotalDraft,
  onInputModeChange,
  onPerTeamChange,
  onTotalChange,
  onApplySuggestion,
}) => {
  const canonicalTotal = divisionTotalFromGamesPerTeam(teamCount, perTeam)
  // Show the user's typed value in the total input while it's invalid
  // so the chip's "this number is wrong" message lines up with what
  // they see; otherwise show the canonical total derived from perTeam.
  const totalInputValue =
    invalidTotalDraft !== undefined ? invalidTotalDraft : canonicalTotal
  const showInvalidChip = invalidTotalDraft !== undefined
  // Closest balanced suggestions only when the draft is invalid.
  const invalidSuggestions = showInvalidChip
    ? closestBalancedDivisionTotals(teamCount, invalidTotalDraft)
    : null

  return (
    <div css={divisionCardStyle}>
      <span css={divisionCardLabelStyle}>
        {divName} · {teamCount} team{teamCount === 1 ? '' : 's'}
      </span>

      <Segmented
        value={inputMode}
        onChange={(v) => onInputModeChange(v as InputMode)}
        options={[
          { label: 'Games per team', value: 'per_team' },
          { label: 'Division total', value: 'division_total' },
        ]}
        disabled={disabled || teamCount < 2}
        size='small'
        css={segmentedToggleStyle}
        block
      />

      {inputMode === 'per_team' ? (
        <>
          <div css={divisionCardInputRowStyle}>
            <span
              css={css`
                ${derivedMathStyle};
                color: ${colors.NEUTRAL_30};
              `}
            >
              Games per team
            </span>
            <InputNumber
              min={0}
              // Untouched → blank: the division fills its feasible slots by
              // default. The derived perTeam is a baseline, not the target.
              value={isUntouched ? undefined : perTeam}
              placeholder='Fills slots'
              onChange={onPerTeamChange}
              disabled={disabled || teamCount < 2}
              css={defaultGamesInputStyle}
            />
          </div>
          <span css={derivedMathStyle}>
            {isUntouched
              ? 'Default: fills available season slots'
              : `Each of ${teamCount} teams plays ${perTeam} game${
                  perTeam === 1 ? '' : 's'
                }`}
          </span>
        </>
      ) : (
        <>
          <div css={divisionCardInputRowStyle}>
            <span
              css={css`
                ${derivedMathStyle};
                color: ${colors.NEUTRAL_30};
              `}
            >
              Division total
            </span>
            <InputNumber
              min={0}
              value={isUntouched ? undefined : totalInputValue}
              placeholder='Fills slots'
              onChange={onTotalChange}
              disabled={disabled || teamCount < 2}
              css={defaultGamesInputStyle}
            />
          </div>
          <span css={derivedMathStyle}>
            {isUntouched
              ? 'Default: fills available season slots'
              : `Each of ${teamCount} teams plays ${
                  showInvalidChip ? '—' : perTeam
                } game${!showInvalidChip && perTeam === 1 ? '' : 's'}`}
          </span>
        </>
      )}

      {showInvalidChip ? (
        <InvalidTotalChip
          totalGames={invalidTotalDraft as number}
          teamCount={teamCount}
          suggestions={invalidSuggestions}
          disabled={disabled}
          onApplySuggestion={onApplySuggestion}
        />
      ) : (
        hasSlotData &&
        feasibility && (
          <FeasibilityChip
            result={feasibility}
            onApplySuggestion={onApplySuggestion}
            disabled={disabled}
          />
        )
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// InvalidTotalChip — shown when the user types a division total in
// `division_total` mode that doesn't divide evenly across teams. Surfaces
// the two closest valid totals as one-click apply buttons. Does not
// reach into editsByDivision; clicking a button routes through the
// existing per-team mutation path.
// ---------------------------------------------------------------------------

interface InvalidTotalChipProps {
  totalGames: number
  teamCount: number
  suggestions: ReturnType<typeof closestBalancedDivisionTotals>
  disabled: boolean
  onApplySuggestion: (perTeam: number) => void
}

const InvalidTotalChip: React.FC<InvalidTotalChipProps> = ({
  totalGames,
  teamCount,
  suggestions,
  disabled,
  onApplySuggestion,
}) => {
  return (
    <div css={feasibilityChipWarnStyle}>
      <p
        css={css`
          margin: 0;
        `}
      >
        ℹ {totalGames} game{totalGames === 1 ? '' : 's'} won&apos;t split evenly
        across {teamCount} teams — the scheduler will balance it within one game
        per team. Use a balanced total below for an exactly-even split.
      </p>
      {suggestions && (
        <div css={feasibilityChipActionsStyle}>
          <Button
            size='small'
            disabled={disabled}
            onClick={() => onApplySuggestion(suggestions.below.perTeam)}
          >
            Use {suggestions.below.total} ({suggestions.below.perTeam}/team)
          </Button>
          <Button
            size='small'
            disabled={disabled}
            onClick={() => onApplySuggestion(suggestions.above.perTeam)}
          >
            Use {suggestions.above.total} ({suggestions.above.perTeam}/team)
          </Button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// FeasibilityChip — three-state chip rendering ✓ / ⚠ / ✗ with
// suggested-apply buttons that the user can click instead of doing math.
// ---------------------------------------------------------------------------

interface FeasibilityChipProps {
  result: FeasibilityResult
  onApplySuggestion: (value: number) => void
  disabled: boolean
}

const FeasibilityChip: React.FC<FeasibilityChipProps> = ({
  result,
  onApplySuggestion,
  disabled,
}) => {
  if (result.kind === 'feasible') {
    // Strict-first FILL dual numbers: the chip's primary count is the STRICT
    // (all-hard) total; show the relaxed alternative only when a satisfiable soft
    // cap would pack more games if relaxed (relaxed per-team > strict per-team).
    // When that relaxed alternative exists, suppress "slots free" (it would count
    // the relaxed-only spare slots as underfill of the strict schedule).
    const { showRelaxed, showSlotsFree } = feasibleChipDisplay(result)
    // Estimate honesty (D4): a local pre-backend/offline verdict says
    // "(estimate)" like the infeasible chip already did; a backend verdict
    // whose partition refinement failed (feasibility_degraded) renders AMBER
    // with an explicit hint — never a confident green on optimistic numbers.
    const isEstimate = result.source === 'estimate'
    const isDegraded = result.degraded === true
    return (
      <div
        css={
          isDegraded ? feasibilityChipWarnStyle : feasibilityChipFeasibleStyle
        }
      >
        <p
          css={css`
            margin: 0;
          `}
        >
          {isDegraded ? '⚠' : '✓'} Feasible
          {isEstimate || isDegraded ? ' (estimate)' : ''} · {result.gamesNeeded}{' '}
          game
          {result.gamesNeeded === 1 ? '' : 's'} on the calendar
          {/* Strict-first FILL: when a relaxed alternative exists, "slots free" is
              the RELAXED ceiling minus the STRICT total (e.g. 252 of 390) — that
              reads as "underfilled" even though the strict schedule is full at its
              all-hard ceiling. Suppress it; the secondary line below carries the
              honest relaxed-capacity context instead. */}
          {showSlotsFree && (
            <>
              {' '}
              · {result.slotsRemaining} of{' '}
              {result.slotsRemaining + result.gamesNeeded} slots free
            </>
          )}
        </p>
        {isDegraded && (
          <p
            css={css`
              margin: 0;
              opacity: 0.85;
            `}
          >
            Verdict degraded — the scheduler couldn&apos;t verify this against
            generation; numbers are estimates.
          </p>
        )}
        {showRelaxed && (
          <p
            css={css`
              margin: 0;
              opacity: 0.85;
            `}
          >
            …or up to {result.relaxedGamesNeeded} games ({result.capacityMax}
            /team) if rules are relaxed
          </p>
        )}
      </div>
    )
  }
  if (result.kind === 'auto-balance-impossible') {
    // Only the BACKEND produces this verdict now (`reason=balance_impossible`);
    // the offline parity gate no longer manufactures it, because the backend
    // rounds an odd total UP rather than refusing it.
    //
    // `gamesNeeded` is the DIVISION TOTAL, not a per-team number — it was being
    // rendered as "{63} per team", which read as a target the user never typed.
    // The Apply buttons below ARE per-team values.
    return (
      <div css={feasibilityChipWarnStyle}>
        <p
          css={css`
            margin: 0;
          `}
        >
          ⚠ That target needs {result.gamesNeeded} game
          {result.gamesNeeded === 1 ? '' : 's'}, which can&apos;t be balanced
          across these teams. Closest per-team targets:
        </p>
        <div css={feasibilityChipActionsStyle}>
          <Button
            size='small'
            disabled={disabled}
            onClick={() => onApplySuggestion(result.closestBelow)}
          >
            Apply {result.closestBelow}
          </Button>
          <Button
            size='small'
            disabled={disabled}
            onClick={() => onApplySuggestion(result.closestAbove)}
          >
            Apply {result.closestAbove}
          </Button>
        </div>
      </div>
    )
  }
  // infeasible
  const isBackend = result.source === 'backend'
  // Backend applies the discounted, solver-safe recommendation; the offline
  // estimate can only honestly apply the raw ceiling (never "recommended").
  const appliedValue = isBackend
    ? result.recommendedMax ?? result.capacityMax
    : result.capacityMax
  // Show the raw ceiling as context only when it actually exceeds the
  // recommendation — never nudge the user toward the 0%-surplus target.
  const showCeiling =
    isBackend &&
    result.recommendedMax !== null &&
    result.capacityMax > result.recommendedMax
  return (
    <div css={feasibilityChipErrorStyle}>
      <p
        css={css`
          margin: 0;
        `}
      >
        ✗ Not enough time slots — {result.gamesNeeded} games needed,{' '}
        {result.slotsAvailable} available.
      </p>
      <div css={feasibilityChipActionsStyle}>
        <Button
          size='small'
          type='primary'
          disabled={disabled || appliedValue < 1}
          onClick={() => onApplySuggestion(appliedValue)}
        >
          {isBackend
            ? `Apply recommended (${appliedValue})`
            : `Apply max (${appliedValue}) (estimate)`}
        </Button>
      </div>
      {showCeiling && (
        <p
          css={css`
            margin: 0;
            opacity: 0.85;
          `}
        >
          Capacity ceiling: {result.capacityMax}/team
        </p>
      )}
    </div>
  )
}

export default MatchupMatrixModal
